/* PFC mount pricing — canonical browser copy of scripts/pfc-mount-pricing.py.
 *
 * ONE model, five consumers. Keep this file and the Python script byte-equivalent
 * in behaviour: the Python generates the catalogue ladders, this prices anything
 * off-ladder at runtime (custom sizes, add-on quotes). If they disagree, the
 * theme quotes a price the cart does not charge.
 *
 *     base(w,h) = SETUP + MATERIAL * area_m2 + TIME * ((w+h)/1000) ** P
 *     premium   = base * PREM_MULT
 *     order     = SUM(component makes) + POSTAGE once [+ ADHERE if a print is mounted]
 *
 * The mount is NEVER discounted for being an add-on. A frame+mount order is
 * always dearer than either part alone; the only saving is that one order posts
 * once, so only the standalone line carries POSTAGE.
 *
 * Superseded (do not reintroduce):
 *   - pfc-print-app.js modelCents/evalModel — least-squares fit over 8 catalogue
 *     prices; invented intercepts and made premium print add-ons dearer than
 *     buying the mount standalone.
 *   - pfc-mount.js customRawPrice — perimeter * 2.96 * 2.333, floor 7.
 *   - pfc-mount.js STRIPE_FACTOR 15/7 — now PREM_MULT.
 *
 * SYNC RULE: change a knob here -> change it in scripts/pfc-mount-pricing.py and
 * regenerate every ladder (`plan` then `apply` then `verify`), same commit.
 */
(function (root) {
  'use strict';

  var KNOBS = {
    GBP: { SETUP: 5.00, MATERIAL: 18.0, TIME: 11.0, P: 1.35, PREM_MULT: 2.1,
           STEP: 0.25, SHIP_UPLIFT: 3.00, ADHERE: 4.00 },
    USD: { SETUP: 9.50, MATERIAL: 24.0, TIME: 15.0, P: 1.35, PREM_MULT: 2.1,
           STEP: 1.00, SHIP_UPLIFT: 11.50, ADHERE: 6.00 }
  };

  // scenario -> [carriesPostage, addsAdhere]
  var SCENARIOS = {
    'frame+mount': [false, false],   // bundle component; the frame line posts
    'standalone':  [true,  false],   // the only line in the order
    'mount+photo': [false, true]     // print line posts; mounting is extra labour
  };

  // Base-store currency. NOT the presentment currency — every ladder and every
  // knob is denominated in the store's own currency, and Shopify converts for
  // other markets. Reading Shopify.currency.active here would mis-price a
  // visitor browsing in EUR.
  function knobsFor(currency) {
    return KNOBS[currency] || KNOBS.GBP;
  }

  function roundUp(x, step) {
    return Math.ceil(x / step) * step;
  }

  // Mount making price, no postage, no assembly labour.
  function basePrice(currency, wMm, hMm) {
    var k = knobsFor(currency);
    var area = (wMm * hMm) / 1e6;
    var span = (wMm + hMm) / 1000;
    return k.SETUP + k.MATERIAL * area + k.TIME * Math.pow(span, k.P);
  }

  /**
   * @param {string} currency  base-store currency ('GBP' | 'USD')
   * @param {number} wMm       EXTERNAL board width (opening is free — single aperture)
   * @param {number} hMm       EXTERNAL board height
   * @param {string} tier      'standard' | 'premium'
   * @param {string} scenario  'standalone' | 'frame+mount' | 'mount+photo'
   * @returns {number} price in MAJOR units, rounded up to the store's step
   */
  function mountPrice(currency, wMm, hMm, tier, scenario) {
    var k = knobsFor(currency);
    var p = basePrice(currency, wMm, hMm);
    if (tier === 'premium') p *= k.PREM_MULT;
    var sc = SCENARIOS[scenario] || SCENARIOS.standalone;
    // Fixed adders last: postage and adhering labour are the same job whatever
    // the size, so neither is scaled by size or by the premium multiplier.
    if (sc[0]) p += k.SHIP_UPLIFT;
    if (sc[1]) p += k.ADHERE;
    return roundUp(p, k.STEP);
  }

  // Premium upcharge over plain at the same size + scenario. This is what the
  // `stripe` Bundle Pro option stores, and what the customisers show as the
  // patterned/printed-text uplift.
  function premiumUplift(currency, wMm, hMm, scenario) {
    return Math.max(0, mountPrice(currency, wMm, hMm, 'premium', scenario)
                     - mountPrice(currency, wMm, hMm, 'standard', scenario));
  }

  var API = {
    KNOBS: KNOBS,
    SCENARIOS: SCENARIOS,
    knobsFor: knobsFor,
    basePrice: basePrice,
    mountPrice: mountPrice,
    premiumUplift: premiumUplift
  };

  if (typeof module === 'object' && module.exports) module.exports = API;
  root.PFCMountPricing = API;
})(typeof window !== 'undefined' ? window : this);
